import { drawCommunicationChartArrowHead, isFiniteCommunicationChartPosition, resolveCommunicationChartArrowDepthPx, resolveCommunicationChartArrowPlacementAtNode } from '@web/components/CommunicationNetworkGraph/graph/communicationChartArrowGeometry';
import { COMMUNICATION_CHART_OPACITY, resolveCommunicationChartArrowGapPx } from '@web/components/CommunicationNetworkGraph/graph/communicationChartConstants';
import { drawCommunicationChartAvatarOverlay } from '@web/components/CommunicationNetworkGraph/graph/drawCommunicationChartAvatarOverlay';
import { drawCommunicationChartEmployeeSectionLabels } from '@web/components/CommunicationNetworkGraph/graph/drawCommunicationChartEmployeeSectionLabels';
import { drawCommunicationChartHubNodeOverlay } from '@web/components/CommunicationNetworkGraph/graph/drawCommunicationChartHubNodeOverlay';
import { drawCommunicationChartOrgTeamLabels } from '@web/components/CommunicationNetworkGraph/graph/drawCommunicationChartOrgTeamLabels';
export { getRenderedCommunicationChartNodeRadiusPx, intersectRayWithNodePerimeter, isFiniteCommunicationChartPosition, resolveEdgeTangentAngleAtEndpoint, tangentAngleFromInnerPointToEndpoint } from '@web/components/CommunicationNetworkGraph/graph/communicationChartArrowGeometry';
var GAP_EPSILON_PX = 0.5;
export var getEdgeRenderedLineWidthPx = function (edge) {
    var _a;
    var renderedWidth = edge.renderedWidth();
    if (Number.isFinite(renderedWidth) && renderedWidth > 0) {
        return renderedWidth;
    }
    return Math.max(Number((_a = edge.data('width')) !== null && _a !== void 0 ? _a : 1), 1);
};
var readEdgeDistanceFromNode = function (edge, side) {
    var _a;
    try {
        var property = side === 'source'
            ? 'source-distance-from-node'
            : 'target-distance-from-node';
        var value = (_a = edge.pstyle(property)) === null || _a === void 0 ? void 0 : _a.pfValue;
        return Number.isFinite(value) ? value : 0;
    }
    catch (_b) {
        return 0;
    }
};
var setEdgeDistanceFromNodeIfChanged = function (edge, side, nextGapPx) {
    var property = side === 'source'
        ? 'source-distance-from-node'
        : 'target-distance-from-node';
    var currentGapPx = readEdgeDistanceFromNode(edge, side);
    if (Math.abs(currentGapPx - nextGapPx) <= GAP_EPSILON_PX) {
        return false;
    }
    edge.style(property, nextGapPx);
    return true;
};
/** Stop the cytoscape stroke at the arrow base (one arrow depth from the node). */
export var syncCommunicationChartEdgeArrowGaps = function (cy) {
    var changed = false;
    cy.batch(function () {
        cy.edges().forEach(function (edge) {
            if (edge.hasClass('hubHidden')) {
                return;
            }
            var lineWidthPx = getEdgeRenderedLineWidthPx(edge);
            var sourceArrowSize = edge.data('sourceArrowSize');
            var targetArrowSize = edge.data('targetArrowSize');
            if (sourceArrowSize !== undefined &&
                setEdgeDistanceFromNodeIfChanged(edge, 'source', resolveCommunicationChartArrowGapPx(sourceArrowSize, lineWidthPx))) {
                changed = true;
            }
            if (targetArrowSize !== undefined &&
                setEdgeDistanceFromNodeIfChanged(edge, 'target', resolveCommunicationChartArrowGapPx(targetArrowSize, lineWidthPx))) {
                changed = true;
            }
        });
    });
    return changed;
};
export var prepareCommunicationChartOverlayCanvas = function (canvas) {
    var ctx = canvas.getContext('2d');
    if (!ctx) {
        return null;
    }
    var width = canvas.clientWidth;
    var height = canvas.clientHeight;
    if (width <= 0 || height <= 0) {
        return null;
    }
    if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
    }
    return { ctx: ctx, width: width, height: height };
};
var edgeHasRenderableEndpoints = function (edge) {
    var source = edge.source();
    var target = edge.target();
    if (source.empty() ||
        target.empty() ||
        source.hasClass('hubHidden') ||
        target.hasClass('hubHidden')) {
        return false;
    }
    return (isFiniteCommunicationChartPosition(source.renderedPosition()) &&
        isFiniteCommunicationChartPosition(target.renderedPosition()));
};
export var drawCommunicationChartArrows = function (cy, ctx) {
    cy.edges().forEach(function (edge) {
        var _a;
        if (edge.hasClass('hubHidden') || !edgeHasRenderableEndpoints(edge)) {
            return;
        }
        var lineWidthPx = getEdgeRenderedLineWidthPx(edge);
        var sourceArrowSize = edge.data('sourceArrowSize');
        var targetArrowSize = edge.data('targetArrowSize');
        var sourceDepthPx = resolveCommunicationChartArrowDepthPx(sourceArrowSize, lineWidthPx);
        var targetDepthPx = resolveCommunicationChartArrowDepthPx(targetArrowSize, lineWidthPx);
        if (sourceDepthPx <= 0 && targetDepthPx <= 0) {
            return;
        }
        var color = String((_a = edge.data('lineColor')) !== null && _a !== void 0 ? _a : '#9CA3AF');
        var opacity = edge.hasClass('dimmed')
            ? COMMUNICATION_CHART_OPACITY.edgeDimmedCanvas
            : 1;
        if (targetDepthPx > 0) {
            var placement = resolveCommunicationChartArrowPlacementAtNode(edge, edge.target(), 'target', targetDepthPx);
            if (placement) {
                drawCommunicationChartArrowHead(ctx, placement.tip, placement.angle, placement.depthPx, color, opacity);
            }
        }
        if (sourceDepthPx > 0) {
            var placement = resolveCommunicationChartArrowPlacementAtNode(edge, edge.source(), 'source', sourceDepthPx);
            if (placement) {
                drawCommunicationChartArrowHead(ctx, placement.tip, placement.angle, placement.depthPx, color, opacity);
            }
        }
    });
};
export var drawCommunicationChartCanvasOverlay = function (cy, canvas) {
    var prepared = prepareCommunicationChartOverlayCanvas(canvas);
    if (!prepared) {
        return;
    }
    var ctx = prepared.ctx, width = prepared.width, height = prepared.height;
    if (syncCommunicationChartEdgeArrowGaps(cy)) {
        return;
    }
    ctx.clearRect(0, 0, width, height);
    drawCommunicationChartAvatarOverlay(cy, ctx);
    drawCommunicationChartHubNodeOverlay(cy, ctx);
    drawCommunicationChartArrows(cy, ctx);
    drawCommunicationChartOrgTeamLabels(cy, ctx);
    drawCommunicationChartEmployeeSectionLabels(cy, ctx);
};
